import { useState } from "react";

import { identity, isEmpty, sortBy } from "lodash/fp";

import Button from "@mui/material/Button";

import {
  CHANNEL_OPTS_FOR_TOPIC_PROVIDER,
  MESSAGE_OPTS_FOR_TOPIC_PROVIDER,
} from "@app/analytics/constants/topics";
import useTopicReport from "@app/analytics/TopicReport/hooks/useTopicReport";
import type { Topic } from "@app/analytics/types";
import useMeasure from "@app/common/hooks/useMeasure";
import { pluralizePhrase } from "@app/common/utils";

import { Column, Row } from "@rui/Stack";

import {
  EmptyOverviewText,
  OverviewButton,
  OverviewHeading,
  OverviewSection,
} from "./styles";

const KEYWORDS_COLLAPSED_HEIGHT = 120; // (34px height of buttons * 3 rows) + (8px between rows * 2) + lil extra

const SourcesOverview = ({ topic }: { topic: Topic }) => {
  const sourcesEmpty =
    topic.provider === "unknown" ||
    (!topic.messageCount && !topic.channelCount);

  if (sourcesEmpty) return null;

  const channelOpts = CHANNEL_OPTS_FOR_TOPIC_PROVIDER[topic.provider];
  const messageOpts = MESSAGE_OPTS_FOR_TOPIC_PROVIDER[topic.provider];

  return (
    <OverviewSection>
      <Row flexWrap="wrap" spacing={1}>
        {topic.channelCount > 0 && channelOpts && (
          <OverviewButton startIcon={<channelOpts.Icon />}>
            {pluralizePhrase(topic.channelCount, channelOpts.sourceLabel)}
          </OverviewButton>
        )}
        {topic.messageCount > 0 && messageOpts && (
          <OverviewButton startIcon={<messageOpts.Icon />}>
            {pluralizePhrase(topic.messageCount, messageOpts.sourceLabel)}
          </OverviewButton>
        )}
      </Row>
    </OverviewSection>
  );
};

const Keywords = ({ keywords }: { keywords: Topic["keywords"] }) => {
  const [viewAllKeywords, setViewAllKeywords] = useState(false);
  const [keywordContainerRef, , { innerHeight }] = useMeasure();

  const toggleViewAll = () => setViewAllKeywords((viewAll) => !viewAll);

  const hasMoreKeywordsToShow = innerHeight > KEYWORDS_COLLAPSED_HEIGHT;

  return (
    <Column spacing={1}>
      <Column
        overflow="hidden"
        maxHeight={
          viewAllKeywords ? undefined : `${KEYWORDS_COLLAPSED_HEIGHT}px`
        }
      >
        <Row flexWrap="wrap" spacing={1} ref={keywordContainerRef}>
          {keywords.map((keyword) => (
            <OverviewButton key={keyword}>{keyword}</OverviewButton>
          ))}
        </Row>
      </Column>
      {hasMoreKeywordsToShow && (
        <Row variant="left">
          <Button onClick={toggleViewAll}>
            View {viewAllKeywords ? "fewer" : "more"}
          </Button>
        </Row>
      )}
    </Column>
  );
};

const TopicReportSidebarOverview = () => {
  const { topic, isSharedReport } = useTopicReport();

  // appease types
  if (!topic) return null;

  const keywords = sortBy(identity, topic.keywords);
  const contributors = sortBy("displayName", topic.contributors);

  return (
    <Column padding={3}>
      <SourcesOverview topic={topic} />
      <OverviewSection>
        <OverviewHeading>Keywords</OverviewHeading>
        {isEmpty(keywords) ? (
          <EmptyOverviewText>No keywords</EmptyOverviewText>
        ) : (
          <Keywords keywords={keywords} />
        )}
      </OverviewSection>
      {!isSharedReport && (
        <OverviewSection>
          <OverviewHeading>Contributors</OverviewHeading>
          {isEmpty(contributors) ? (
            <EmptyOverviewText>No contributors</EmptyOverviewText>
          ) : (
            <Row flexWrap="wrap" spacing={1}>
              {contributors.map((contributor) => (
                <OverviewButton key={contributor.id}>
                  {contributor.displayName}
                </OverviewButton>
              ))}
            </Row>
          )}
        </OverviewSection>
      )}
    </Column>
  );
};

export default TopicReportSidebarOverview;
